import { useState, useMemo } from "react"
import { Layout } from "../components/Layout"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import { useToast } from "@/components/Toast"
import { useAffiliates, useCreateAffiliate } from "@/hooks/useAffiliates"
import type { Affiliate } from "@/hooks/useAffiliates"
import { APIError } from "@/api"
import {
  GiftIcon,
  PlusIcon,
  CurrencyDollarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"

const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`

export function Affiliates() {
  const [page, setPage] = useState(0)
  const [pageSize] = useState(20)
  const [search, setSearch] = useState("")
  const { toast } = useToast()

  const { data, isLoading, error, refetch } = useAffiliates({
    page: page + 1,
    pageSize,
    search,
  })

  const createAffiliate = useCreateAffiliate()

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [createForm, setCreateForm] = useState({
    name: "",
    contactEmail: "",
    payoutEmail: "",
    refCode: "",
    commissionPercent: "10",
    adminNotes: "",
  })

  const handleAffiliateClick = (publicId: string) => {
    window.location.href = `/affiliates/${publicId}`
  }

  const handleCopyReferralLink = async (e: React.MouseEvent, url: string) => {
    e.stopPropagation() // don't also trigger the row's navigate-to-detail click
    try {
      await navigator.clipboard.writeText(url)
      toast({ title: "Link copied", description: "Referral link copied to clipboard." })
    } catch (_error) {
      toast({
        title: "Copy failed",
        description: "Unable to copy this link from the browser.",
        variant: "destructive",
      })
    }
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(0) // a new search invalidates whatever page we were on
  }

  const onCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.name.trim() || !createForm.contactEmail.trim() || !createForm.refCode.trim())
      return
    try {
      await createAffiliate.mutateAsync({
        name: createForm.name.trim(),
        contactEmail: createForm.contactEmail.trim(),
        payoutEmail: (createForm.payoutEmail.trim() || createForm.contactEmail.trim()),
        refCode: createForm.refCode.trim(),
        commissionPercent: Number(createForm.commissionPercent),
        adminNotes: createForm.adminNotes.trim() || undefined,
      })
      toast({
        title: "Affiliate created",
        description: `${createForm.name} · ref code "${createForm.refCode.trim().toLowerCase()}"`,
      })
      setCreateForm({
        name: "",
        contactEmail: "",
        payoutEmail: "",
        refCode: "",
        commissionPercent: "10",
        adminNotes: "",
      })
      setShowCreateDialog(false)
      refetch()
    } catch (e) {
      toast({
        title: "Error creating affiliate",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const columns = useMemo<ColumnDef<Affiliate>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Affiliate",
        cell: ({ row }) => (
          <div className="flex items-center">
            <GiftIcon className="h-5 w-5 text-secondary mr-2" />
            <div>
              <div className="font-medium text-primary">{row.original.name}</div>
              <div className="text-xs text-secondary">{row.original.contact_email}</div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "ref_code",
        header: "Ref code",
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <code className="text-sm bg-background/40 border border-border rounded px-1.5 py-0.5">
              {row.original.ref_code}
            </code>
            {/* The disabled Button is pointer-events-none, so a click on it falls through
                to whatever's underneath (the row's onRowClick) unless THIS wrapper (which
                stays clickable) stops it first. */}
            <span onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={row.original.status !== "active"}
                title={
                  row.original.status !== "active"
                    ? "Inactive affiliate, signups wouldn't be attributed"
                    : undefined
                }
                onClick={(e) => void handleCopyReferralLink(e, row.original.referralUrl)}
              >
                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              </Button>
            </span>
          </div>
        ),
      },
      {
        accessorKey: "commission_percent",
        header: "Commission",
        cell: ({ row }) => <span className="text-sm">{row.original.commission_percent}%</span>,
      },
      {
        accessorKey: "totalReferred",
        header: "Referred",
        cell: ({ row }) => <span className="text-sm">{row.original.totalReferred}</span>,
      },
      {
        accessorKey: "totalOwedCents",
        header: "Owed",
        cell: ({ row }) => (
          <div className="flex items-center">
            <CurrencyDollarIcon
              className={`h-4 w-4 mr-1 ${row.original.totalOwedCents > 0 ? "text-amber-400" : "text-secondary"}`}
            />
            <span
              className={`text-sm ${row.original.totalOwedCents > 0 ? "font-medium text-amber-400" : "text-secondary"}`}
            >
              {formatCents(row.original.totalOwedCents)}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              row.original.status === "active"
                ? "bg-green-900/30 text-green-400"
                : "bg-gray-800 text-secondary"
            }`}
          >
            {row.original.status}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Affiliates</h1>
            <p className="text-secondary">
              Referral partners, their ref codes, and commissions owed
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            New affiliate
          </Button>
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : data?.affiliates?.length === 0 ? (
          <EmptyState
            icon={<GiftIcon className="h-12 w-12" />}
            title="No affiliates found"
            description="No affiliate referral partners have been created yet."
            action={{
              label: "Create first affiliate",
              onClick: () => setShowCreateDialog(true),
            }}
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={data?.affiliates || []}
              searchKey="name"
              searchValue={search}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Search by name, ref code, or email..."
              showPagination={false}
              onRowClick={(affiliate) => handleAffiliateClick(affiliate.public_id)}
            />
            {data?.pagination && (
              <div className="flex items-center justify-between text-sm text-secondary">
                <span>
                  Page {data.pagination.page} of {Math.max(data.pagination.totalPages, 1)} ·{" "}
                  {data.pagination.totalCount} affiliate{data.pagination.totalCount === 1 ? "" : "s"}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    disabled={!data.pagination.hasPreviousPage}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    disabled={!data.pagination.hasNextPage}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New affiliate</DialogTitle>
            <DialogDescription>
              Generates a referral link (?ref=&lt;code&gt;) that attributes new signups to this
              affiliate; a commission is credited automatically when a referred team's first
              payment succeeds.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreateSubmit} className="space-y-4">
            <Input
              label="Name"
              placeholder="Coach Mike"
              value={createForm.name}
              onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))}
              required
            />
            <Input
              label="Contact email"
              type="email"
              placeholder="mike@example.com"
              value={createForm.contactEmail}
              onChange={(e) => setCreateForm((s) => ({ ...s, contactEmail: e.target.value }))}
              required
            />
            <Input
              label="Payout email (optional, defaults to contact email)"
              type="email"
              placeholder="mike-paypal@example.com"
              value={createForm.payoutEmail}
              onChange={(e) => setCreateForm((s) => ({ ...s, payoutEmail: e.target.value }))}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Ref code"
                placeholder="coach-mike"
                value={createForm.refCode}
                onChange={(e) => setCreateForm((s) => ({ ...s, refCode: e.target.value }))}
                required
              />
              <Input
                label="Commission %"
                type="number"
                min={0}
                max={100}
                value={createForm.commissionPercent}
                onChange={(e) =>
                  setCreateForm((s) => ({ ...s, commissionPercent: e.target.value }))
                }
                required
              />
            </div>
            <p className="text-xs text-secondary -mt-2">
              Ref code becomes part of the shareable link. 3-32 characters, letters/numbers/-/_.
              Not editable after creation, deactivate and create a new affiliate instead.
            </p>
            <Input
              label="Admin notes (optional)"
              placeholder="How this partnership was arranged"
              value={createForm.adminNotes}
              onChange={(e) => setCreateForm((s) => ({ ...s, adminNotes: e.target.value }))}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  createAffiliate.isPending ||
                  !createForm.name.trim() ||
                  !createForm.contactEmail.trim() ||
                  !createForm.refCode.trim()
                }
              >
                {createAffiliate.isPending ? "Creating..." : "Create affiliate"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
